import React, { useContext } from 'react';

import { Sparkles } from 'lucide-react';

import { reloadCompany } from 'common/actions/company';
import AJAX from 'common/AJAX';
import Pill from 'common/common/Pill';
import { ShowToastContext, ToastTypes } from 'common/containers/ToastContainer';
import connect from 'common/core/connect';
import LazyLoadedImage from 'common/LazyLoadedImage';
import Markdown from 'common/markdown/Markdown';
import ModalPortal from 'common/modals/ModalPortal';
import ButtonV2 from 'common/ui/ButtonV2';
import { H1, P } from 'common/ui/Text';
import parseAPIResponse, { isDefaultSuccessResponse } from 'common/util/parseAPIResponse';
import styles from 'css-module/components/modals/_CannyAnnouncementModal.module.scss';

type AnnouncementProps = {
  body: string;
  cta: string;
  image: string;
  learnMore?: string;
  onDismiss?: () => void;
  onLearnMoreClick?: () => void;
  onSuccess: () => void;
  title: string;
  tag: string;
};

export const CannyAnnouncement = ({
  body,
  cta,
  image,
  learnMore,
  onDismiss,
  onLearnMoreClick,
  onSuccess,
  title,
  tag,
}: AnnouncementProps) => {
  return (
    <div>
      <div className={styles.header}>
        <LazyLoadedImage src={image} />
      </div>
      <section className={styles.modalContent}>
        <Pill className={styles.pill}>
          <Sparkles strokeWidth={2} size={14} />
          {tag}
        </Pill>
        <H1 variant="headingXl">{title}</H1>
        <div className={styles.body}>
          <Markdown contents={body} />
        </div>
        <footer className={styles.footer}>
          <div className={styles.right}>
            {onDismiss && (
              <button className={styles.skipButton} onClick={onDismiss}>
                <P>Dismiss</P>
              </button>
            )}
            {cta && (
              <ButtonV2 size="medium" onClick={onSuccess}>
                {cta}
              </ButtonV2>
            )}
          </div>
          {learnMore ? (
            <a
              href={learnMore}
              target="_blank"
              rel="nofollow noopener"
              className={styles.learnMore}
              onClick={onLearnMoreClick}>
              Learn more
            </a>
          ) : null}
        </footer>
      </section>
    </div>
  );
};

type ModalProps = {
  name: string;
};

type ConnectProps = {
  reloadCompany: () => Promise<void>;
};

type OwnProps = AnnouncementProps & ModalProps;
type Props = OwnProps & ConnectProps;

const CannyAnnouncementModal = ({
  body,
  cta,
  image,
  learnMore,
  name,
  onLearnMoreClick,
  onSuccess,
  reloadCompany,
  title,
  tag,
}: Props) => {
  const showToast = useContext(ShowToastContext);

  const close = async () => {
    const response = await AJAX.post('/api/nux/dismissNUX', { nuxType: name });

    const { error } = parseAPIResponse(response, { isSuccessful: isDefaultSuccessResponse });
    if (error) {
      showToast(error.message, ToastTypes.error);
      return;
    }

    reloadCompany?.();
  };

  const succeed = async () => {
    await close();
    onSuccess();
  };

  return (
    <ModalPortal modalClassName={styles.modal} onClose={close} closeOnClickAway allowEscape>
      <CannyAnnouncement
        body={body}
        cta={cta}
        image={image}
        learnMore={learnMore}
        onDismiss={close}
        onLearnMoreClick={onLearnMoreClick}
        onSuccess={succeed}
        title={title}
        tag={tag}
      />
    </ModalPortal>
  );
};

export default connect(null, { reloadCompany })(
  CannyAnnouncementModal,
) as unknown as React.FC<OwnProps>;
